import { useCreateSubscriptionRule } from "admin/finance/hooks/useCreateSubscriptionRule";
import { useGetActivityLabels } from "../hooks/useGetActivityLabels";
import { QUERY_KEY_SUBSCRIPTION_RULES } from "admin/finance/hooks/useGetSubscriptionRules";
import { TPackage } from "admin/finance/types";
import { Form, Input, InputNumber, Modal, Select } from "antd";
import { AppButton } from "components/button/AppButton";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import { generalValidationRules } from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";

type Props = IdentifierProps & { packages: TPackage[] | undefined };

export const AddSubscriptionRule = ({
  open,
  handleClose,
  packages,
}: Props) => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreateSubscriptionRule();
  const { data: activityLabels } = useGetActivityLabels();
  const handleSubmit = (values: {
    description: string;
    label: string;
    package_id: string;
    feature: string;
    count: number;
  }) => {
    mutate(
      {
        token,
        description: values.description,
        label: values.label,
        package_id: values.package_id,
        feature: values.feature,
        count: values.count,
      },
      {
        onError: (err: any) => {
          const res = err.response?.data;
          let title = "Error";
          let description = "An error occurred, please try again.";
          if (res?.message && typeof res.message === "string") {
            description = res.message;
          }
          if (res?.errors && typeof res.errors === "object") {
            const first = Object.values(res.errors)[0];
            if (Array.isArray(first) && first[0]) {
              description = String(first[0]);
            }
          }
          openNotification({
            title,
            state: "error",
            description,
            duration: 6,
          });
        },
        onSuccess: () => {
          openNotification({
            title: "Success",
            state: "success",
            description: "Subscription rule created successfully",
            duration: 4.5,
          });
          form.resetFields();
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_SUBSCRIPTION_RULES],
          });
          handleClose();
        },
      }
    );
  };

  return (
    <Modal
      title="Add subscription rule"
      open={open}
      onCancel={() => handleClose()}
      footer={null}
      width="40rem"
      style={{ top: 10 }}
      destroyOnClose
    >
      <Form
        layout="vertical"
        requiredMark={false}
        form={form}
        onFinish={handleSubmit}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5">
          
          
          <Form.Item
            name="label"
            label="Label"
            rules={generalValidationRules}
          >
            <Select
              placeholder="Select label"
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {activityLabels?.map((l: any) => (
                <Select.Option key={l} value={l}>
                  {l.toUpperCase()}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="feature" label="Feature" rules={generalValidationRules}>
            <Input placeholder="Feature key" />
          </Form.Item>
          <Form.Item
            name="package_id"
            label="Package"
            rules={generalValidationRules}
          >
            <Select
              placeholder="Select package"
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {packages?.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.name} ({p.account_type})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="count"
            label="Count"
            rules={[{ required: true, message: "Count is required" }]}
          >
            <InputNumber className="w-full" min={0} placeholder="0" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={generalValidationRules}
            className="md:col-span-2"
          >
            <Input.TextArea rows={2} placeholder="Short description" />
          </Form.Item>
        </div>
        <AppButton type="submit" isLoading={isLoading} />
      </Form>
    </Modal>
  );
};
